import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { AuthTokenService } from '../auth/auth-token.service';
import { UiService } from '../services/ui.service';
import { SessionContextService } from '../session-context.service';
import { environment } from 'src/environments/environment';

/**
 * Responsibilities:
 *  1. Attach the Bearer token to every outgoing request.
 *  2. On 401 → warn, clear auth storage, and redirect to /auth/login (preserving returnUrl).
 *  3. On 403 → warn that the account/role lacks permission (session stays intact).
 *  4. On 5xx → emit to UiService.errors$ for global error UI.
 *
 * IMPORTANT: Must NOT inject AuthService here. AuthService depends on HttpClient,
 * which would create a circular dependency: interceptor → AuthService → HttpClient → interceptor.
 * Instead inject AuthTokenService (no HttpClient dep) and Router directly.
 */
// Module-level (not per-injection) so concurrent 401s from a single page load
// only trigger one redirect instead of racing and nesting returnUrl into itself.
// Exported so AuthTokenService can re-arm it once a new session starts (see
// setUser() there) — otherwise a second session expiry later in the same tab
// would silently stop redirecting after the first one.
import {
  markSessionExpiredRedirect,
  clearSessionExpiredRedirect,
  isSessionExpiredRedirect,
} from '../services/session-expiry.state';

export function resetRedirectingToLoginGuard(): void {
  clearSessionExpiredRedirect();
}

// Track the last *committed* route rather than reading router.url at error
// time: on a hard/direct navigation, resolver-driven HTTP calls (and the
// 401s they trigger) can fire before the Router finishes committing the
// target route, so router.url still reflects the previous URL. NavigationEnd
// always reflects a fully-resolved URL (including query params), so it is
// safe to read from a 401 handler regardless of timing. Seed from the
// current location so a 401 on the very first request (before any
// NavigationEnd has fired yet) still captures the right path.
let lastCommittedUrl =
  typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';

// Subscribed once per app lifetime (module-level), same rationale as
// the session-expiry flag above — this is not per-request state.
let navTrackingStarted = false;

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authToken = inject(AuthTokenService);
  const router = inject(Router);
  const uiService = inject(UiService);
  const sessionContext = inject(SessionContextService);

  if (!navTrackingStarted) {
    navTrackingStarted = true;
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        lastCommittedUrl = event.urlAfterRedirects;
      }
    });
  }

  const token = authToken.getToken() ?? '';
  const oldSessionId = sessionContext.getSessionId();
  const isSessionApi = req.url.startsWith(`${environment.apiUrl}/session`);
  // v2 backend rejects oldSessionId on these endpoints (strict param validation)
  const skipOldSession = req.url.includes('/transportFees/get-all-submit-transportfee');

  const authReq = req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`),
    setParams: !isSessionApi && !skipOldSession && oldSessionId ? { oldSessionId } : {},
  });

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        authToken.clearUser();
        // Guard stays set for the rest of this page load once tripped — a
        // burst of parallel requests can each 401 well after the first one's
        // client-side navigate() has already resolved, so resetting the flag
        // on navigation completion (rather than on next real login) let later
        // 401s in the same burst sneak through and re-navigate with
        // returnUrl already pointing at /auth/login.
        if (!isSessionExpiredRedirect()) {
          // Toast BEFORE arming the flag — the flag suppresses all later
          // snackbars (component error handlers would otherwise replace this
          // message with the raw backend text).
          uiService.openSnackbar('Your session has expired. Please log in again.', '', 'error');
          markSessionExpiredRedirect();
          const returnUrl = lastCommittedUrl;
          const isLoginPage = returnUrl.startsWith('/auth/login');
          router.navigate(['/auth/login'], {
            queryParams: !isLoginPage ? { returnUrl } : {},
          });
        }
      } else if (err.status === 403) {
        uiService.openSnackbar(
          err.error?.message ?? "You don't have permission to do this.",
          '',
          'error',
        );
      } else if (err.status >= 500) {
        uiService.emitError({
          status: err.status,
          message: err.error?.message ?? 'An unexpected server error occurred.',
        });
      }
      return throwError(() => err);
    }),
  );
};
